/** @file Accordion with `<Switch />` in header */

/* eslint-disable react/jsx-props-no-spreading */

import React from 'react';
import {
  Accordion as MuiAccordion,
  AccordionProps,
  AccordionDetails,
  AccordionSummary,
  Box,
  SxProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Switch from '../../../common/Switch';
import { useMerchant } from '../../../contexts/MerchantContext';

const styles: { [is: string]: SxProps } = {
  accTitle: {
    color: 'text.primary',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '4px',
  },
  accTitleAndIconContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  accDescription: {
    color: 'text.secondary',
    fontSize: '10px',
    lineHeight: '16px',
  },
  accDescriptionCallout: {
    fontWeight: 'bold',
  },
  accSummary: {
    width: '100%',
    paddingLeft: '16px !important',
    paddingRight: '24px !important',
    '& > div': {
      justifyContent: 'space-between',
    },
  },
};

interface Props extends AccordionProps {
  /** Any passed children become part of `<AccordionSummary />` */
  children: AccordionProps['children'];
  /** Test ID */
  'data-testid'?: string;
  /** Whether or not the accordion is open */
  isExpanded: boolean;
  /** Whether or not the switch component is toggled on */
  isSwitchChecked: boolean;
  /** Handler for expand/collapse change */
  onExpandToggle: AccordionProps['onChange'];
  /** Handler for switch change */
  onSwitchToggle: () => any;
  /** SX overrides for base `<Accordion />` component */
  sx?: SxProps;
  /** Accordion title */
  title: string;
}

/**
 * Accordion use in payments and processing
 * @param {Props} props Component properties
 * @returns {JSX.Element}
 */
const Accordion = ({
  children,
  isExpanded,
  isSwitchChecked,
  onExpandToggle,
  onSwitchToggle,
  sx = {},
  title,
  ...accordionProps
}: Props): JSX.Element => {
  const { canUpdateMerchantSettings } = useMerchant();
  return (
    <MuiAccordion
      expanded={isExpanded}
      onChange={(event, ...args) => {
        if (!event?.target) {
          return;
        }
        const { target } = event;
        const className = (target as HTMLElement).getAttribute('class');
        const isAccordionClick =
          Boolean(target) && className
            ? className.includes('MuiAccordionSummary') || !className.includes('gc-switch')
            : false;

        if (isAccordionClick) {
          onExpandToggle?.(event, ...args);
        }
      }}
      sx={sx}
      {...accordionProps}
    >
      <AccordionSummary sx={styles.accSummary}>
        <Box sx={styles.accTitleAndIconContainer}>
          <ExpandMoreIcon
            data-testid={`${accordionProps['data-testid'] || ''}-ACCORDION-EXPAND_TOGGLE`}
            sx={isExpanded ? { transform: 'rotate(180deg)' } : {}}
          />
          <Box sx={styles.accTitle}>{title}</Box>
        </Box>
        <Switch
          data-testid={`${accordionProps['data-testid'] || ''}-ACCORDION-SWITCH`}
          isChecked={isSwitchChecked}
          onToggle={onSwitchToggle}
          isDisabled={!canUpdateMerchantSettings()}
          aria-label={`${title} Toggle`}
        />
      </AccordionSummary>
      <AccordionDetails sx={styles.accDescription}>{children}</AccordionDetails>
    </MuiAccordion>
  );
};

export default Accordion;
